import React from "react"
import PropTypes from "prop-types"
import { Spacer } from "gatsby-interface"
import { OrganizationPlanBadge } from "@modules/organization/shared/components/OrganizationPlanBadge"
import { TrialRemainingDays } from "@modules/billing/components/TrialRemainingDays"
import { isTrialingPlan } from "@modules/billing/shared/utils"
import { useFlags } from "@modules/featureFlags"
import { PlanTierInfo } from "@modules/billing/shared/components/PlanTierInfo"

const trialingDetailsRow = theme => ({
  [theme.mediaQueries.phablet]: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    flexDirection: "row",
  },
})

const propTypes = {
  plan: PropTypes.object,
  rteSummary: PropTypes.object,
  context: PropTypes.oneOf([`card`, `header`]),
}

const rteSpanCss = theme => ({
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
})

const rteStrongCss = theme => ({
  color: theme.colors.grey[80],
  fontSize: theme.fontSizes[1],
})

const planTiersCss = theme => ({
  display: `grid`,

  rowGap: theme.space[2],
  columnGap: theme.space[10],

  [theme.mediaQueries.desktop]: {
    gridTemplateColumns: `max-content max-content`,
  },
})

function RTEDisplay({ count, limit, interval }) {
  return (
    <span css={rteSpanCss}>
      <strong css={rteStrongCss}>
        {count}/{limit}
      </strong>{" "}
      RTEs used this {interval}
    </span>
  )
}

export default function PlanSnippet({ rteSummary, billing }) {
  const { flags } = useFlags()
  const planName = billing?.plan?.name
  const remainingDays = billing?.trialDaysRemaining
  const billingStatus = billing?.status
  const buildsTier = billing?.plan?.buildsTier
  const hostingTier = billing?.plan?.hostingTier

  const interval = `month`
  const count = rteSummary?.RTEsInCurrentPeriod || 0
  const showOrganizationBadge = Boolean(billingStatus && planName)

  if (!flags.multiTiersPlans && isTrialingPlan(billingStatus)) {
    return (
      <div>
        <div css={trialingDetailsRow}>
          <span data-testid="org-plan-snippet__badge">
            {showOrganizationBadge && (
              <OrganizationPlanBadge
                billingStatus={billingStatus}
                planName={planName}
              />
            )}
          </span>

          <Spacer size={3} direction="horizontal" />

          <TrialRemainingDays days={remainingDays} noTooltip={true} />
        </div>

        {rteSummary && (
          <React.Fragment>
            <Spacer size={2} />
            <RTEDisplay
              count={count}
              limit={rteSummary.limit}
              interval={interval}
            />
          </React.Fragment>
        )}
      </div>
    )
  }

  return flags.multiTiersPlans ? (
    <div>
      <div>
        {isTrialingPlan(billingStatus) && (
          <TrialRemainingDays
            days={remainingDays}
            noTooltip={true}
            css={theme => ({
              marginBottom: theme.space[8],
            })}
          />
        )}
        <div
          css={{
            marginBottom: 5,
          }}
        >
          Billing Interval:{" "}
          <strong>
            {billing.plan.interval === `MONTHLY` ? `MONTHLY` : `YEARLY`}
          </strong>
        </div>
        <div css={planTiersCss}>
          <PlanTierInfo
            tierType="builds"
            tier={buildsTier}
            showDetails={true}
          />
          <PlanTierInfo
            tierType="hosting"
            tier={hostingTier}
            showDetails={true}
          />
        </div>
      </div>
    </div>
  ) : (
    <div css={trialingDetailsRow}>
      <span data-testid="org-plan-snippet__badge">
        {showOrganizationBadge && (
          <OrganizationPlanBadge
            billingStatus={billingStatus}
            planName={planName}
          />
        )}
      </span>
      <Spacer size={3} direction="horizontal" />
      {rteSummary && (
        <RTEDisplay
          count={count}
          limit={rteSummary.limit}
          interval={interval}
        />
      )}
    </div>
  )
}

PlanSnippet.propTypes = propTypes
